// lib/resolvePageBySlug.ts
import { DEFAULT_LOCALE } from '@/config/app.config';
import serverClient, { browserClient } from './apolloClient';
import { GET_PAGE_BY_SLUG } from './queries/getPageBySlug';
import { GET_URL_SUB_DIRECTORY } from './queries/getUrlSubDirectory';
import { MEETING_PLACE_PAGE_Occasion_QUERY } from './queries/meetingPlacePageOccasionListQuery';
import { MEETING_PLACE_PAGE_TENANT_LIST_ID_QUERY } from './queries/meetingPlacePageTenantListQuery';
import { unstable_cache } from 'next/cache';
import { isBrowser } from '@/helpers/is-browser';

// In the browser, route through the same-origin /api/graphql proxy so the
// Contentful token is never exposed. On the server, call Contentful directly.
const client = isBrowser ? browserClient : serverClient;

// Mapping from list page content types to their corresponding detail content types
const LIST_TO_DETAIL_CONTENT_TYPE_MAP: Record<string, string> = {
  MeetingPlacePageOfferList: 'ContentfulMeetingPlacePageOfferItem',
  MeetingPlacePageNewsList: 'ContentfulMeetingPlacePageNewsItem',
  MeetingPlacePageEventList: 'ContentfulMeetingPlacePageEventItem',
  MeetingPlacePageLeisurePackageOfferList:
    'ContentfulMeetingPlacePageLeisurePackageOfferItem',
  MeetingPlacePageTenantShopList: 'ContentfulMeetingPlacePageTenantShopItem',
  MeetingPlacePageTenantEntertainmentList:
    'ContentfulMeetingPlacePageTenantEntertainmentItem',
  MeetingPlacePageTenantFoodbeverageList:
    'ContentfulMeetingPlacePageTenantFoodBevItem',
  MeetingPlacePageServiceList: 'ContentfulMeetingPlacePageServiceItem',
  // MeetingPlacePageServiceListCollection:
  //   'ContentfulLibraryServiceType',
};

// export async function resolvePageBySlug({
async function resolvePageBySlugUncached({
  slug,
  locale,
  tagId,
  preview = false,
}: {
  slug: string;
  locale: string;
  tagId: string;
  preview: boolean;
}) {
  const FALLBACK_LOCALE = DEFAULT_LOCALE;

  // Try primary locale
  let slugResult = await fetchBySlug(slug, locale, locale, tagId, preview);

  //  Try fallback locale (English)
  if (!slugResult && locale !== FALLBACK_LOCALE) {
    slugResult = await fetchBySlug(
      slug,
      FALLBACK_LOCALE,
      locale,
      tagId,
      preview,
    );
  }

  // No slug result → return null (STOP HERE)
  if (!slugResult) return null;

  return slugResult;
}

// Cached version for production
export async function resolvePageBySlug({
  slug,
  locale,
  tagId,
  preview = false,
}: {
  slug: string;
  locale: string;
  tagId: string;
  preview: boolean;
}) {
  // Don't cache in preview mode, and never use the server-only cache in the
  // browser (unstable_cache is server-only).
  if (preview || isBrowser) {
    return resolvePageBySlugUncached({ slug, locale, tagId, preview });
  }

  try {
    return await unstable_cache(
      async () => resolvePageBySlugUncached({ slug, locale, tagId, preview }),
      [`page-by-slug-${slug}-${locale}-${tagId}`],
      {
        revalidate: 600,
        tags: [`page-${slug}`, `locale-${locale}`, `tag-${tagId}`],
      },
    )();
  } catch (error) {
    // Fallback to uncached version if incremental cache is not available
    // This can happen in edge runtime, build time, or certain deployment contexts
    console.warn(
      '[resolvePageBySlug] Cache unavailable, falling back to uncached version:',
      error instanceof Error ? error.message : 'Unknown error',
    );
    return resolvePageBySlugUncached({ slug, locale, tagId, preview });
  }
}

async function fetchBySlug(
  slug: string,
  locale: string,
  pageLocale: string,
  tagId: string,
  preview: boolean,
) {
  const { data }: any = await client.query({
    query: GET_PAGE_BY_SLUG,
    variables: { slug, locale, tagId, preview },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });

  const found = await extractFromSlugCollections(
    data,
    locale,
    pageLocale,
    tagId,
  );
  if (!found) return null;

  return {
    type: found.type,
    data: found.data,
    urlSubdirectory: found.urlSubdirectory,
    urlSubdirectoryNews: found.urlSubdirectoryNews,
    urlSubdirectoryEvent: found.urlSubdirectoryEvent,
    urlSubdirectoryOffer: found.urlSubdirectoryOffer,
    listSourceId: found.listSourceId,
  };
}

// Shape for SLUG-based queries (collection → items array)
async function extractFromSlugCollections(
  data: any,
  locale: string,
  pageLocale: string,
  tagId: string,
) {
  if (!data) return null;

  const collections = [
    ...(data?.meetingPlacePageTenantShopListCollection?.items || []),
    ...(data?.meetingPlacePageTenantFoodbeverageListCollection?.items || []),
    ...(data?.meetingPlacePageTenantEntertainmentListCollection?.items || []),
    ...(data?.meetingPlacePageServiceListCollection?.items || []),
    ...(data?.meetingPlacePageOfferListCollection?.items || []),
    ...(data?.meetingPlacePageEditorialCollection?.items || []),
    ...(data?.meetingPlacePageFindUsCollection?.items || []),
    ...(data?.meetingPlacePageLegalDocumentCollection?.items || []),
    ...(data?.meetingPlacePageEditorialFullWidthCollection?.items || []),
    ...(data?.meetingPlacePageEditorialListCollection?.items || []),
    ...(data?.meetingPlacePageNewsListCollection?.items || []),
    ...(data?.meetingPlacePageEventListCollection?.items || []),
    ...(data?.meetingPlacePageOfferCollection?.items || []),
    ...(data?.meetingPlacePageLeisurePackageOfferListCollection?.items || []),
    ...(data?.meetingPlacePageOccasionsCollection?.items || []),
    ...(data?.meetingPlacePageTenantListCollection?.items || []),
    ...(data?.meetingPlacePageFloorPlanCollection?.items || []),
    ...(data?.meetingPlacePageSearchResultsCollection?.items || []),
    ...(data?.meetingPlacePageIframeCollection?.items || []),
  ];

  const found = collections[0] || null;
  if (!found) return null;

  const type: string = found.__typename;

  // Get URL subdirectory for list pages that have detail pages
  let urlSubdirectory: string | null = null;
  let urlSubdirectoryNews: string | null = null;
  let urlSubdirectoryEvent: string | null = null;
  let urlSubdirectoryOffer: string | null = null;
  const detailContentType = LIST_TO_DETAIL_CONTENT_TYPE_MAP[type];

  if (type === 'MeetingPlacePageOccasions') {
    try {
      const { data: urlData }: any = await client.query({
        query: GET_URL_SUB_DIRECTORY,
        variables: {
          locale: pageLocale,
        },
      });

      const urlSubDirectoryCollection =
        urlData?.libraryUrlSubdirectoryCollection?.items;

      if (urlSubDirectoryCollection && urlSubDirectoryCollection.length > 0) {
        // Check for ContentfulMeetingPlacePageNewsItem
        const newsItemSubDir = urlSubDirectoryCollection.find((item: any) =>
          item?.contentTypes?.includes('ContentfulMeetingPlacePageNewsItem'),
        );

        if (newsItemSubDir?.urlSubdirectory) {
          urlSubdirectoryNews = newsItemSubDir.urlSubdirectory;
        }

        // Check for ContentfulMeetingPlacePageEventItem
        const eventItemSubDir = urlSubDirectoryCollection.find((item: any) =>
          item?.contentTypes?.includes('ContentfulMeetingPlacePageEventItem'),
        );

        if (eventItemSubDir?.urlSubdirectory) {
          urlSubdirectoryEvent = eventItemSubDir.urlSubdirectory;
        }

        const offerItemSubDir = urlSubDirectoryCollection.find((item: any) =>
          item?.contentTypes?.includes('ContentfulMeetingPlacePageOfferItem'),
        );

        if (offerItemSubDir?.urlSubdirectory) {
          urlSubdirectoryOffer = offerItemSubDir.urlSubdirectory;
        }

        // Set urlSubdirectory to NewsItem if available, otherwise EventItem (for backward compatibility)
        urlSubdirectory = urlSubdirectoryNews || urlSubdirectoryEvent;
      }
    } catch (error) {
      console.warn('Error fetching URL subdirectory:', error);
    }
  } else if (detailContentType) {
    try {
      const { data: urlData }: any = await client.query({
        query: GET_URL_SUB_DIRECTORY,
        variables: {
          locale: pageLocale,
        },
      });

      const urlSubDirectoryCollection =
        urlData?.libraryUrlSubdirectoryCollection?.items;

      if (urlSubDirectoryCollection && urlSubDirectoryCollection.length > 0) {
        const urlSubDirItem = urlSubDirectoryCollection.find((item: any) =>
          item?.contentTypes?.includes(detailContentType),
        );

        if (urlSubDirItem?.urlSubdirectory) {
          urlSubdirectory = urlSubDirItem.urlSubdirectory;
        }
      }
    } catch (error) {
      console.warn('Error fetching URL subdirectory:', error);
    }
  }

  let listSourceId: string | null = null;

  const isOccationList =
    detailContentType == 'ContentfulMeetingPlacePageOfferItem' ||
    detailContentType == 'ContentfulMeetingPlacePageNewsItem' ||
    detailContentType == 'ContentfulMeetingPlacePageEventItem';

  const isTenantList =
    detailContentType == 'ContentfulMeetingPlacePageTenantShopItem' ||
    detailContentType == 'ContentfulMeetingPlacePageTenantEntertainmentItem' ||
    detailContentType == 'ContentfulMeetingPlacePageTenantFoodBevItem';

  if (isOccationList || isTenantList) {
    try {
      const { data }: any = await client.query({
        query: isOccationList
          ? MEETING_PLACE_PAGE_Occasion_QUERY
          : MEETING_PLACE_PAGE_TENANT_LIST_ID_QUERY,
        variables: {
          locale: pageLocale,
          tagId,
        },
      });

      listSourceId =
        data?.meetingPlacePageOccasionsCollection?.items[0]?.sys?.id ||
        data?.meetingPlacePageTenantListCollection?.items[0]?.sys?.id;
    } catch (error) {
      console.warn('Error Fetching Occation: ', error);
    }
  }

  return {
    type,
    data: found,
    urlSubdirectory,
    urlSubdirectoryNews,
    urlSubdirectoryEvent,
    urlSubdirectoryOffer,
    listSourceId,
  };
}
